import React, {
  CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  CloseButton,
  Container,
  Dialog,
  Divider,
  Modal,
  Pressable,
  Row,
} from "../components";
import { AvailableTimeSlot } from "../offering/api";
import { FirstDayOfWeek, parseDay, toDay } from "../lib/datetime";
import { useI18n } from "../lib/i18n/i18n_provider";
import { Button } from "./button";
import { DatePicker } from "./date_picker";
import { formatMeetingRoomDateRange } from "./format_date_range";
import { MeetingRoomDateRange } from "./order_params";
import { PickerView } from "./picker_view";
import { Spacer } from "./spacer";
import { TimeRangePickerV2 } from "./time_range_picker_v2";
import { addHours, parseTime, TimeInterval } from "./time_utils";
import { TimePicker } from "./time_picker";
import { useAnalytics } from "../lib/analytics/analytics";

export interface MeetingRoomDateRangePickerProps {
  value?: MeetingRoomDateRange;
  onComplete: (dateRange?: MeetingRoomDateRange) => void;
  ctaTitle: string;
  firstDayOfWeek?: FirstDayOfWeek;
  disabled?: boolean;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate?: (date: Date) => boolean;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onSelectDate: (date: Date) => void;
  placeholder?: string;
  timeslots?: AvailableTimeSlot[];
  view: "modal" | "dialog";
  isRequiredTimeRange?: boolean;
  numberOfMonths?: number;
  minStartTime?: string;
  maxEndTime?: string;
  timeSlotIncrement?: number;
  override?: {
    root?: { style?: CSSProperties };
    view?: { style?: CSSProperties };
    arrow?: { style?: CSSProperties };
  };
}

export function MeetingRoomDateRangePicker(
  props: MeetingRoomDateRangePickerProps
) {
  const {
    value,
    onComplete,
    view,
    isOutsideRange,
    ctaTitle,
    firstDayOfWeek,
    disabled,
    isBlockedDate,
    onNextVisibleDates,
    onSelectDate,
    placeholder,
    timeslots,
    isRequiredTimeRange,
    numberOfMonths,
    minStartTime,
    maxEndTime,
    timeSlotIncrement,
    override,
  } = props;
  const [open, setOpen] = useState(false);
  const { fbt, locale } = useI18n();
  const analytics = useAnalytics();

  useEffect(() => {
    if (disabled) {
      onComplete(undefined);
      setOpen(false);
    }
  }, [disabled, onComplete]);

  return (
    <Fragment>
      <Pressable
        style={{
          width: "100%",
          height: "100%",
          textAlign: "initial",
        }}
        onPress={() => !disabled && setOpen(true)}
      >
        <PickerView
          icon="calendar"
          disabled={disabled}
          title={
            !disabled && value?.date
              ? formatMeetingRoomDateRange(value, fbt, locale)
              : fbt(placeholder || "When")
          }
          override={{
            root: override?.view,
            arrow: override?.arrow,
          }}
        />
      </Pressable>
      <DateTimePickerView
        firstDayOfWeek={firstDayOfWeek}
        value={value}
        open={open}
        onClose={() => setOpen(false)}
        onComplete={(v) => {
          onComplete(v);
          analytics.event(
            "Save Date Click",
            {
              "Booking Start Date": v?.date,
              "Booking End Date": v?.date,
              "Booking Start Time": v?.startTime,
              "Booking End Time": v?.endTime,
              Source: "WL",
            },
            { onlyProviders: ["mixpanel", "customerio"] }
          );
        }}
        view={view}
        onSelectDate={onSelectDate}
        isOutsideRange={isOutsideRange}
        onNextVisibleDates={onNextVisibleDates}
        ctaTitle={ctaTitle}
        isBlockedDate={isBlockedDate}
        timeslots={timeslots}
        isRequiredTimeRange={isRequiredTimeRange}
        numberOfMonths={numberOfMonths}
        minStartTime={minStartTime}
        maxEndTime={maxEndTime}
        timeSlotIncrement={timeSlotIncrement}
      />
    </Fragment>
  );
}

const timeRangePickerOverride = {
  view: { style: { paddingTop: 8, paddingBottom: 8 } },
};

interface DateTimePickerViewProps {
  firstDayOfWeek?: FirstDayOfWeek;
  value: MeetingRoomDateRange | undefined;
  onComplete: (dateRange?: MeetingRoomDateRange) => void;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onSelectDate: (date: Date) => void;
  ctaTitle: string;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate?: (date: Date) => boolean;
  view: "modal" | "dialog";
  open: boolean;
  onClose: () => void;
  timeslots?: AvailableTimeSlot[];
  isRequiredTimeRange?: boolean;
  numberOfMonths?: number;
  minStartTime?: string;
  maxEndTime?: string;
  timeSlotIncrement?: number;
}

function DateTimePickerView(props: DateTimePickerViewProps) {
  const {
    value,
    open,
    onComplete,
    onClose,
    view,
    isOutsideRange,
    ctaTitle,
    isBlockedDate,
    onNextVisibleDates,
    onSelectDate,
    timeslots,
    firstDayOfWeek,
    isRequiredTimeRange = true,
    numberOfMonths = 2,
    minStartTime = "0800",
    maxEndTime = "2000",
    timeSlotIncrement,
  } = props;

  const [localValue, setLocalValue] = useState(value);
  const { fbt } = useI18n();
  const analytics = useAnalytics();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const { handleDateChange, handleTimeRangeChange, date, start, end } =
    useMeetingRoomDateRangeHelpers({
      value: localValue,
      onChange: setLocalValue,
    });
  const handleTimePickerChange = function (
    startTime?: string,
    endTime?: string
  ) {
    if (date) {
      setLocalValue({
        date: toDay(date),
        endTime,
        startTime,
      });
      const meetingRoomFilter = {
        start: startTime,
        end: endTime,
      };
      handleTimeRangeChange(meetingRoomFilter);

      analytics.event(
        "Time Select Click",
        {
          "Start Time": startTime,
          "End Time": endTime,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    }
  };

  const handleLocalDateChange = useCallback(
    (date: Date) => {
      handleDateChange(date);
      onSelectDate(date);

      analytics.event(
        "Date Select Click",
        {
          Date: toDay(date),
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [analytics, onSelectDate, handleDateChange]
  );

  return (
    <Fragment>
      {view === "modal" ? (
        <Modal isOpen={open} onRequestClose={onClose}>
          <Container expanded paddingHorizontal={16} paddingBottom={16}>
            <Row>
              <CloseButton onPress={onClose} />
            </Row>
            <div style={{ flex: 1, overflow: "auto" }}>
              <DatePicker
                firstDayOfWeek={firstDayOfWeek}
                value={date}
                onChange={handleLocalDateChange}
                isBlocked={isBlockedDate}
                isOutsideRange={isOutsideRange}
                onNextVisibleDates={onNextVisibleDates}
                numberOfMonths={numberOfMonths}
                orientation="vertical"
              />
            </div>
            <Spacer size={24} />
            <Divider />
            <Spacer size={8} />
            {isRequiredTimeRange && timeslots ? (
              <TimeRangePickerV2
                timeslots={timeslots}
                disabled={!date}
                value={{ start, end }}
                onChange={(value) => {
                  handleTimeRangeChange(value);

                  analytics.event(
                    "Time Select Click",
                    {
                      "Start Time": value.start,
                      "End Time": value.end,
                      Source: "WL",
                    },
                    { onlyProviders: ["mixpanel", "customerio"] }
                  );
                }}
                override={timeRangePickerOverride}
              />
            ) : (
              <Row>
                <TimePicker
                  value={start}
                  placeholder={fbt("Start time")}
                  startTime={minStartTime}
                  endTime={maxEndTime}
                  step={timeSlotIncrement}
                  onChange={(startTime) =>
                    handleTimePickerChange(startTime, end)
                  }
                  formatter={(time) => fbt.formatTime(parseTime(time))}
                  disabled={!date}
                />
                <Spacer size={30} direction="horizontal" />
                <TimePicker
                  value={end}
                  step={timeSlotIncrement}
                  placeholder={fbt("End time")}
                  startTime={addHours(start || minStartTime, 1)}
                  endTime={maxEndTime}
                  onChange={(endTime) => handleTimePickerChange(start, endTime)}
                  formatter={(time) => fbt.formatTime(parseTime(time))}
                  disabled={!start}
                />
              </Row>
            )}
            <Spacer size={16} />
            <Row justifyContent="flex-end">
              <Button
                disabled={isRequiredTimeRange ? !(date && start && end) : !date}
                onPress={() => {
                  onClose();
                  onComplete(localValue);
                }}
                title={ctaTitle}
              />
            </Row>
          </Container>
        </Modal>
      ) : (
        <Dialog isOpen={open} onRequestClose={onClose}>
          <Container
            className="meeting-room-date-time-picker__dialog"
            width={numberOfMonths === 2 ? 700 : 350}
            padding={16}
          >
            <DatePicker
              firstDayOfWeek={firstDayOfWeek}
              value={date}
              onChange={handleLocalDateChange}
              isBlocked={isBlockedDate}
              isOutsideRange={isOutsideRange}
              numberOfMonths={numberOfMonths}
              onNextVisibleDates={onNextVisibleDates}
              orientation="horizontal"
            />
            <Spacer size={24} />
            <div>
              {isRequiredTimeRange && timeslots ? (
                <TimeRangePickerV2
                  disabled={!date}
                  value={{ start, end }}
                  onChange={(value) => {
                    handleTimeRangeChange(value);

                    analytics.event(
                      "Time Select Click",
                      {
                        "Start Time": value.start,
                        "End Time": value.end,
                        Source: "WL",
                      },
                      { onlyProviders: ["mixpanel", "customerio"] }
                    );
                  }}
                  timeslots={timeslots}
                  override={timeRangePickerOverride}
                />
              ) : (
                <Row justifyContent="space-between">
                  <TimePicker
                    value={start}
                    placeholder={fbt("Start time")}
                    step={timeSlotIncrement}
                    startTime={minStartTime}
                    endTime={maxEndTime}
                    onChange={(startTime) =>
                      handleTimePickerChange(startTime, end)
                    }
                    formatter={(time) => fbt.formatTime(parseTime(time))}
                    disabled={!date}
                  />
                  <Spacer size={30} direction="horizontal" />
                  <TimePicker
                    value={end}
                    placeholder={fbt("End time")}
                    step={timeSlotIncrement}
                    startTime={addHours(start || minStartTime, 1)}
                    endTime={maxEndTime}
                    onChange={(endTime) =>
                      handleTimePickerChange(start, endTime)
                    }
                    formatter={(time) => fbt.formatTime(parseTime(time))}
                    disabled={!start}
                  />
                </Row>
              )}
              <Spacer size={24} />
              <Row justifyContent="flex-end">
                <Button
                  disabled={
                    isRequiredTimeRange ? !(date && start && end) : !date
                  }
                  onPress={() => {
                    onClose();
                    onComplete(localValue);
                  }}
                  title={ctaTitle}
                  override={{
                    button: { style: { paddingLeft: 40, paddingRight: 40 } },
                  }}
                />
              </Row>
            </div>
          </Container>
        </Dialog>
      )}
    </Fragment>
  );
}

interface UseMeetingRoomDateRangeHelpersProps {
  value?: MeetingRoomDateRange;
  onChange: (value: MeetingRoomDateRange) => void;
}

export function useMeetingRoomDateRangeHelpers(
  props: UseMeetingRoomDateRangeHelpersProps
) {
  const { value, onChange } = props;

  const handleDateChange = useCallback(
    (date: Date) => {
      onChange({
        date: toDay(date),
        startTime: undefined,
        endTime: undefined,
      });
    },
    [onChange]
  );

  const handleTimeRangeChange = useCallback(
    (timeRange: Partial<TimeInterval>) => {
      onChange({
        date: value?.date,
        startTime: timeRange.start,
        endTime: timeRange.end,
      });
    },
    [onChange, value]
  );

  const date = value?.date ? parseDay(value.date) : undefined;

  return {
    handleDateChange,
    handleTimeRangeChange,
    date,
    start: value?.startTime,
    end: value?.endTime,
  };
}
